<!-- TEMPLATE -->
<template>
  <div class="wrapper">
    <dean-dialog
      title="Importeer Odido-abonnementen"
      :large="false"
      :addClose="true"
      @close="close"
    >
      <slot name="load">
      </slot>

      <dean-notification :is-modal="true">
      </dean-notification>

      <fade-transition group>
        <div class="dean-form-row" key="importFile">
          <div class="row">
            <div class="small-6 medium-6 large-6 hd-6 columns">
              <p class="input-label">Odido bestelregels importeren <span style="color:red">*</span></p>
            </div>

            <div class="small-6 medium-6 large-6 hd-6 columns">
              <dean-input-file id="importFile" label="filename"
                               @file-changed="importFileChanged"
                               :requiredField="true">
              </dean-input-file>
            </div>
          </div>
        </div>

        <div class="dean-form-row-errors" v-if="errors.length" key="errors">
            <div class="alert alert-danger" role="alert">
              <ol class="mb-0 pl-3">
                <li v-for="(error, index) in errors" :key="index">
                  {{ error.message }}
                </li>
              </ol>
          </div>
        </div>
      </fade-transition>

      <span slot="dean-dialog-button-group">
        <dean-button :danger="true" :dialog-button="true" @click.native="close()">Annuleren</dean-button>
        <dean-button :success="true" :dialog-button="true" @click.native="save()">Versturen</dean-button>
      </span>
    </dean-dialog>
  </div>
</template>

<!-- SCRIPT -->
<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import axios from 'axios';
import {
    // SVG
    warningIcon,
    // COMPONENTS
    deanDialog,
    deanDialogButtonGroup,
    deanInputFile
  } from '@deanone/dean-components';
import deanButton from '@/components/deanComponents/deanButton.vue';
import DeanNotification from '@/components/deanComponents/deanNotification.vue';
import {deanDialogBus} from '@/main';
import {validationMixin} from 'vuelidate';
import {required} from 'vuelidate/lib/validators';
import {Validations} from 'vuelidate-property-decorators';
import {createNotification} from '@/mixins/validations';
import {FadeTransition} from 'vue2-transitions';
import {xlsx} from '@/mixins/validators';

@Component({
    components: {
      warningIcon,
      DeanNotification,
      deanDialog,
      deanButton,
      deanInputFile,
      deanDialogButtonGroup,
      FadeTransition,
    },
    mixins: [validationMixin],
  })

  export default class ImportOdidoSubscriptionsDialog extends Vue {
    @Validations()
    get validations() {
      const valData: any = {
        data: {
          importFile: {
            required,
            xlsx,
          },
        },
      };

      return valData;
    }

    // DATA
    @Prop() public payloadData!: {
      [key: string]: {
        [key: string]: string,
      };
    };

    public data: {[key: string]: any} = {
      importFile: null,
    };

    public errors: Array<{
      code: string,
      message: string,
    }> = [];

    // METHODS
    public close() {
      deanDialogBus.$emit('close');
    }

    public save() {
      // @ts-ignore-next-line
      this.$v.data.$touch();

      if (!this.$v.$invalid) {
        this.$emit('setLoad', true);

        this.errors = [];

        const formData: FormData = new FormData();
        formData.append('customerId', this.payloadData.customerId.toString());
        formData.append('file', this.data.importFile);

        axios.post('/-/trade/api/customer.orders.odidoSubscriptions.import', formData).then(
            (response) => {

              deanDialogBus.$emit('saved');

              this.$toast(this.$store.state.main.notification.messages.odidoSubscriptionsImported);

              this.close();

              this.$router.push({ path: '/orders/' + response.data.orderId});
            },
            (error) => {
              const errorResponse = error.response.data;

              if (errorResponse.errors?.code) {
                let errorMessage = '';

                switch (errorResponse.errors.code) {
                  case 'INACTIVE_CUSTOMER':
                    errorMessage = 'Je mag geen abonnementen importeren voor een inactieve klant.';
                    break;

                  case 'ERR_WRONG_MIME_TYPE':
                    errorMessage = 'Alleen het Excel-bestandstype wordt ondersteund.';
                    break;

                  case 'ERR_INVALID_HEADINGS':
                    errorMessage = 'Ongeldig Excel-formaat. Ongeldige kolomkoppen of een lege kolom gedetecteerd. Geldige kolomkoppen in de juiste volgorde zijn: ' +
                        'Product Code, Msisdn, Sim Number, Sim Card Type, Connection Amount Price, Recurrent Amount Price, Disconnect Amount Price, Main Parent Product Row.';

                    this.errors.push({
                      code: 'ERR_INVALID_HEADINGS',
                      message: errorMessage
                    });

                    return;
                }

                if (errorMessage) {
                  this.$toast({
                    icon: warningIcon,
                    text: errorMessage,
                    theme: 'danger',
                    title: 'Fout',
                  });

                  return;
                }
              }

              if (Array.isArray(errorResponse)) {
                errorResponse.forEach((error: any) => {
                  let errorMessage = ''

                  switch(error.errorCode) {
                    case 'ERR_EMPTY_PRODUCT_CODE':
                      errorMessage = 'Rij: ' + error.errorRow + '. Productcode is vereist.';
                      break;

                    case 'ERR_PRODUCT_CODE_INVALID_DATA_TYPE':
                      errorMessage = 'Rij: ' + error.errorRow + '. De waarde van de productcode moet van het type string zijn. Formateer uw kolom naar het TYPE TEXT.';
                      break;

                    case 'ERR_INVALID_PRODUCT_CODE':
                      errorMessage = 'Rij: ' + error.errorRow + '. Ongeldige productcode. Er bestaat geen product met de opgegeven code.';
                      break;

                    case 'ERR_EMPTY_PRODUCT_SERVICE':
                      errorMessage = 'Rij: ' + error.errorRow + '. Lege dienst (groep) voor product. Er is geen dienst (groep) gekoppeld aan het opgegeven product.';
                      break;

                    case 'ERR_EMPTY_MSISDN':
                      errorMessage = 'Rij: ' + error.errorRow + '. MSISDN is vereist.';
                      break;

                    case 'ERR_MSISDN_INVALID_DATA_TYPE':
                      errorMessage = 'Rij: ' + error.errorRow + '. De waarde van MSISDN moet van het type string zijn. Formateer uw kolom naar het TYPE TEXT.';
                      break;

                    case 'ERR_INVALID_MSISDN':
                      errorMessage = 'Rij: ' + error.errorRow + '. Ongeldig MSISDN-formaat.';
                      break;

                    case 'ERR_MSISDN_NOT_REQUIRED':
                      errorMessage = 'Rij: ' + error.errorRow + '. MSISDN is niet vereist voor dit product.';
                      break;

                    case 'ERR_DUPLICATED_MSISDN':
                      const duplicatedMsisdnRow = error.errorMessage.match(/\d+/)[0];

                      errorMessage = 'Rij: ' + error.errorRow + '. MSISDN wordt al gebruikt in rij:' + duplicatedMsisdnRow;
                      break;

                    case 'ERR_ASSIGNED_MSISDN':
                      errorMessage = 'Rij: ' + error.errorRow + '.  MSISDN is al toegewezen aan de gefactureerde orderregel.';
                      break;

                    case 'ERR_EMPTY_SIM_NUMBER':
                      errorMessage = 'Rij: ' + error.errorRow + '. Simkaartnummer is vereist.';
                      break;

                    case 'ERR_SIM_NUMBER_INVALID_DATA_TYPE':
                      errorMessage = 'Rij: ' + error.errorRow + '. De waarde van het simnummer moet van het type string zijn. Formateer uw kolom naar het TYPE TEXT.';
                      break;

                    case 'ERR_SIM_NUMBER_NOT_REQUIRED':
                      errorMessage = 'Rij: ' + error.errorRow + '. Sim-nummer is niet vereist voor dit product.';
                      break;

                    case 'ERR_INVALID_SIM_NUMBER':
                      errorMessage =  'Rij: ' + error.errorRow + '. Ongeldig simkaartnummer.';
                      break;

                    case 'ERR_DUPLICATED_SIM_NUMBER':
                      const duplicatedSimNumberRow = error.errorMessage.match(/\d+/)[0];

                      errorMessage = 'Rij: ' + error.errorRow + '. Simkaartnummer wordt al gebruikt in rij:' + duplicatedSimNumberRow;
                      break;

                    case 'ERR_ASSIGNED_SIM_NUMBER':
                      errorMessage =  'Rij: ' + error.errorRow + '. Simkaartnummer is al toegewezen aan de gefactureerde orderregel.';
                      break;

                    case 'ERR_EMPTY_SIM_CARD_TYPE':
                      errorMessage =  'Rij: ' + error.errorRow + '. Type simkaart is vereist.';
                      break;

                    case 'ERR_SIM_CARD_TYPE_INVALID_DATA_TYPE':
                      errorMessage =  'Rij: ' + error.errorRow + '. De waarde van het simkaarttype moet van het type string zijn. Formateer uw kolom naar het TYPE TEXT.';
                      break;

                    case 'ERR_INVALID_SIM_CARD_TYPE':
                      errorMessage =  'Rij: ' + error.errorRow + '. Ongeldig type simkaart. Dit moet e_sim of physical_sim zijn.';
                      break;

                    case 'ERR_SIM_CARD_TYPE_NOT_REQUIRED':
                      errorMessage =  'Rij: ' + error.errorRow + '. Simkaarttype is niet vereist voor dit product.';
                      break;

                    case 'ERR_EMPTY_SIM_TYPE':
                      errorMessage =  'Rij: ' + error.errorRow + '. Simtype is vereist.';
                      break;

                    case 'ERR_SIM_TYPE_INVALID_DATA_TYPE':
                      errorMessage =  'Rij: ' + error.errorRow + '. De waarde van het simtype moet van het type string zijn. Bijvoorbeeld: voice. Formateer uw kolom naar het TYPE TEXT.';
                      break;

                    case 'ERR_INVALID_SIM_TYPE':
                      errorMessage =  'Rij: ' + error.errorRow + '. Ongeldig simtype. Het simtype moet zijn: voice, data of mbsharing. Formateer uw kolom naar het TYPE TEXT.';
                    break;

                    case 'ERR_SIM_TYPE_NOT_REQUIRED':
                      errorMessage =  'Rij: ' + error.errorRow + '. Simtype is niet vereist voor dit product.';
                    break;

                    case 'ERR_INVALID_PRICE_FORMAT':
                      errorMessage =  'Rij: ' + error.errorRow + '. Ongeldig prijsformaat, het moet ofwel een positief getal zijn of een positief decimaal getal gescheiden door een punt met twee decimaal.';
                      break;

                    case 'ERR_INVALID_MIN_RECURRENT_AMOUNT_PRICE':
                      errorMessage =  'Rij: ' + error.errorRow + '. Maandelijkse prijs moet hoger zijn dan 0.';
                      break;

                    case 'ERR_INVALID_MAX_RECURRENT_AMOUNT_PRICE':
                      errorMessage =  'Rij: ' + error.errorRow + '. Maandelijkse prijs moet lager zijn dan 100.';
                      break;

                    case 'ERR_MAIN_PRODUCT_NOT_FOUND':
                      errorMessage =  'Rij: ' + error.errorRow + '. Hoofdproductregel voor add-on niet gevonden.';
                      break;

                    case 'ERR_ADD_ON_ATTACHED_TO_NOT_MAIN_PRODUCT':
                      errorMessage =  'Rij: ' + error.errorRow + '. Add-on moet aan het hoofdproduct worden bevestigd.';
                      break;

                    case 'ERR_ADD_ON_CAN_NOT_BE_ASSIGNED_TO_ITSELF':
                      errorMessage =  'Rij: ' + error.errorRow + '. Toevoeging kan niet aan zichzelf worden toegewezen.';
                      break;

                    case 'ERR_MAIN_PRODUCT_ASSIGNED_AS_ADD_ON':
                      errorMessage =  'Rij: ' + error.errorRow + '. Het hoofdproduct is toegewezen als een add-on.';
                      break;

                    case 'ERR_INVALID_MAIN_PARENT_PRODUCT_CODE':
                      errorMessage = 'Rij: ' + error.errorRow + '. Ongeldige hoofdproductcode. Er bestaat geen product met de opgegeven code.';
                      break;

                    case 'ERR_EMPTY_ROW':
                      errorMessage = 'Rij: ' + error.errorRow + '. Lege rij gedetecteerd. Lege rijen zijn niet toegestaan.';
                      break;

                    case 'ERR_ONLY_MOBILE_PRODUCT_ALLOWED':
                      errorMessage = 'Rij: ' + error.errorRow + '. Je mag alleen mobiele abonnementen importeren.';
                      break;

                    case 'ERR_2_SIMS_PRODUCT_NOT_ALLOWED':
                      errorMessage = 'Rij: ' + error.errorRow + '. Producten met twee simkaarten zijn niet toegestaan.';
                      break;

                    case 'ERR_INVOICE_DATE_EMPTY':
                      errorMessage = 'Rij: ' + error.errorRow + '. Factuurdatum  is vereist.';
                      break;

                    case 'ERR_INVOICE_DATE_INVALID_DATA_TYPE':
                      errorMessage = 'Rij: ' + error.errorRow + '. De waarde van de factuurdatum moet van het type string zijn. Bijvoorbeeld: 20/01/2025. Formateer uw kolom naar het TYPE TEXT.';
                      break;

                    case 'ERR_INVALID_INVOICE_DATE_FORMAT':
                      errorMessage = 'Rij: ' + error.errorRow + '. Factuurdatum heeft een ongeldig formaat. Het formaat moet d/m/Y zijn, bijvoorbeeld 01/01/2024.';
                      break;

                    case 'ERR_INVALID_ADD_ON_DATE':
                      errorMessage = 'Rij: ' + error.errorRow + '. De toevoegingsfactuurdatum moet hetzelfde zijn als de factuurdatum van het hoofdproduct.';
                      break;

                    case 'ERR_INCORRECT_SIM_TYPE_FOR_PRODUCT':
                      const type = error.errorMessage.match(/type should be:\s*(\w+)/i)[1];

                      errorMessage = 'Rij: ' + error.errorRow + '. Onjuiste simtype voor het opgegeven product. De simtype moet zijn: ' + type;
                      break;
                  }

                  this.errors.push({
                    code: error.errorCode,
                    message: errorMessage
                  });
                });
              }
            },
        ).finally(() => {
          this.$emit('setLoad', false);
        });
      } else {
        this.$toast(createNotification(this.$v));
      }
    }

    public importFileChanged(uploadFileData: any) {
      this.data.importFile = uploadFileData.get('file');
    }

    public created() {
      this.$emit('setLoad', false);
    }
  }
</script>

<!-- STYLE -->
<style lang="scss" scoped>

.dialog :deep(.dean-dialog-container) {
  width: 600px;
}

:deep(.dean-input-file .input-container) {
  padding-top: 6px;
}

:deep(.dean-input-file label) {
  display: none !important;
}

.input-label {
  padding-left: 20px;
}

.dean-form-row-errors {
  margin-top: 15px;
  margin-left: 15px;
  margin-right: 15px;
}

</style>
